import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  MapPin, Phone, Mail, Globe, Clock, ArrowLeft, 
  Bookmark, BookmarkCheck, ShieldCheck, Star, Camera, Pencil, StickyNote, Save,
  Users, UserPlus, UserCheck
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { StarRating, RatingDisplay } from "../components/StarRating";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const ServiceProfile = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [isInMySubs, setIsInMySubs] = useState(false);
  const [addingToSubs, setAddingToSubs] = useState(false);

  // Review form state
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  // Edit image state
  const [editImageOpen, setEditImageOpen] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [savingImage, setSavingImage] = useState(false);

  // Personal notes state
  const [personalNote, setPersonalNote] = useState("");
  const [editingNote, setEditingNote] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [existingNote, setExistingNote] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Check auth
        let loggedInUser = null;
        try {
          const authRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
          loggedInUser = authRes.data;
          setUser(authRes.data);

          // Check if saved
          const savedRes = await axios.get(`${API}/saved-services`, { withCredentials: true });
          setIsSaved(savedRes.data.some((s) => s.service_id === serviceId));

          // Check if in My Subs
          try {
            const subsCheck = await axios.get(`${API}/my-subs/check/${serviceId}`, { withCredentials: true });
            setIsInMySubs(subsCheck.data.is_sub);
          } catch (e) {
            // Not in subs
          }

          // Fetch personal note
          try {
            const noteRes = await axios.get(`${API}/notes/${serviceId}`, { withCredentials: true });
            if (noteRes.data) {
              setExistingNote(noteRes.data);
              setPersonalNote(noteRes.data.note || "");
            }
          } catch (e) {
            // No note exists
          }
        } catch (error) {
          // Not logged in
        }

        // Fetch service
        const svcRes = await axios.get(`${API}/services/${serviceId}`);
        setService(svcRes.data);

        // Fetch reviews
        const revRes = await axios.get(`${API}/reviews/${serviceId}`);
        setReviews(revRes.data);
      } catch (error) {
        console.error("Error fetching service:", error);
        toast.error("Service not found");
        navigate("/directory");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [serviceId, navigate]);

  const handleSaveNote = async () => {
    if (!user) {
      toast.error("Please sign in to save notes");
      return;
    }

    setSavingNote(true);
    try {
      const res = await axios.post(
        `${API}/notes/${serviceId}`,
        { note: personalNote },
        { withCredentials: true }
      );
      setExistingNote(res.data);
      setEditingNote(false);
      toast.success(personalNote ? "Note saved!" : "Note deleted");
    } catch (error) {
      toast.error("Failed to save note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleSaveToggle = async () => {
    if (!user) {
      toast.error("Please sign in to save services");
      return;
    }

    try {
      if (isSaved) {
        await axios.delete(`${API}/saved-services/${serviceId}`, { withCredentials: true });
        setIsSaved(false);
        toast.success("Removed from saved");
      } else {
        await axios.post(`${API}/saved-services/${serviceId}`, {}, { withCredentials: true });
        setIsSaved(true);
        toast.success("Service saved!");
      }
    } catch (error) {
      toast.error("Failed to update saved services");
    }
  };

  const handleAddToMySubs = async () => {
    if (!user) {
      toast.error("Please sign in to add to My Subs");
      return;
    }

    setAddingToSubs(true);
    try {
      if (isInMySubs) {
        await axios.delete(`${API}/my-subs/service/${serviceId}`, { withCredentials: true });
        setIsInMySubs(false);
        toast.success("Removed from My Subs");
      } else {
        await axios.post(`${API}/my-subs`, { service_id: serviceId }, { withCredentials: true });
        setIsInMySubs(true);
        toast.success("Added to My Subs! Manage your list in the dashboard.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update My Subs");
    } finally {
      setAddingToSubs(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in to leave a review");
      return;
    }
    if (reviewRating === 0) {
      toast.error("Please select a rating");
      return;
    }

    setSubmittingReview(true);
    try {
      const res = await axios.post(
        `${API}/reviews`,
        {
          service_id: serviceId,
          rating: reviewRating,
          comment: reviewComment,
        },
        { withCredentials: true }
      );

      setReviews([res.data, ...reviews]);
      setShowReviewForm(false);
      setReviewRating(0);
      setReviewComment("");
      toast.success("Review submitted!");

      // Refresh service to get updated rating
      const svcRes = await axios.get(`${API}/services/${serviceId}`);
      setService(svcRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const hasUserReviewed = reviews.some((r) => r.user_id === user?.user_id);
  
  // Check if this is the user's own service - must explicitly have both IDs and they must match
  const isOwnService = Boolean(
    user?.user_id && 
    service?.provider_id && 
    service.provider_id === user.user_id
  );
  
  const canReview = user && service && !isOwnService && !hasUserReviewed;
  const canEdit = user && (user.role === "admin" || isOwnService);

  const handleSaveImage = async (e) => {
    e.preventDefault();
    setSavingImage(true);

    try {
      const res = await axios.put(
        `${API}/services/${serviceId}`,
        { image_url: newImageUrl },
        { withCredentials: true }
      );
      setService(res.data);
      setEditImageOpen(false);
      setNewImageUrl("");
      toast.success("Image updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update image");
    } finally {
      setSavingImage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]">
        <Header user={user} />
        <div className="pt-24 flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!service) return null;

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="service-profile-page">
      <Header user={user} />

      <main className="pt-24 lg:pt-28 pb-16 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <Link
            to="/directory"
            className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1A4D2E] mb-6 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Directory
          </Link>

          {/* Service Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden mb-8">
            {/* Hero Image */}
            <div className="relative h-64 lg:h-80 bg-stone-100">
              {service.image_url ? (
                <img
                  src={service.image_url}
                  alt={service.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1A4D2E]/10 to-[#1A4D2E]/5">
                  <span className="text-8xl font-bold text-[#1A4D2E]/20">
                    {service.name.charAt(0)}
                  </span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              
              {/* Badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                {service.is_featured && (
                  <Badge className="bg-[#E07A5F] text-white border-0">
                    <Star className="w-3 h-3 mr-1 fill-current" />
                    Featured
                  </Badge>
                )}
                {service.is_approved && (
                  <Badge className="bg-[#1A4D2E] text-white border-0">
                    <ShieldCheck className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>

              {/* Edit Image Button (for admin/owner) */}
              {canEdit && (
                <Dialog open={editImageOpen} onOpenChange={setEditImageOpen}>
                  <DialogTrigger asChild>
                    <Button
                      size="sm"
                      className="absolute bottom-4 left-4 bg-[#1A4D2E] hover:bg-[#143D24] text-white shadow-lg"
                      data-testid="edit-image-btn"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      {service.image_url ? "Change Photo" : "Add Photo"}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Update Service Photo</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveImage} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="imageUrl">Image URL</Label>
                        <Input
                          id="imageUrl"
                          type="url"
                          value={newImageUrl}
                          onChange={(e) => setNewImageUrl(e.target.value)}
                          placeholder="https://example.com/image.jpg"
                          className="mt-1.5"
                          data-testid="image-url-input"
                        />
                        <p className="text-xs text-[#71717A] mt-2">
                          Enter a direct URL to an image (JPG, PNG, WebP)
                        </p>
                      </div>
                      {newImageUrl && (
                        <div className="rounded-lg overflow-hidden border border-stone-200">
                          <img
                            src={newImageUrl}
                            alt="Preview"
                            className="w-full h-40 object-cover"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                      <div className="flex gap-3">
                        <Button
                          type="submit"
                          disabled={savingImage || !newImageUrl}
                          className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full"
                          data-testid="save-image-btn"
                        >
                          {savingImage ? "Saving..." : "Save Photo"}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setEditImageOpen(false);
                            setNewImageUrl("");
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}

              {/* Save Button */}
              <Button
                variant="secondary"
                size="icon"
                onClick={handleSaveToggle}
                className="absolute top-4 right-4 bg-white/90 hover:bg-white"
                data-testid="save-btn"
              >
                {isSaved ? (
                  <BookmarkCheck className="w-5 h-5 text-[#1A4D2E]" />
                ) : (
                  <Bookmark className="w-5 h-5 text-[#4A4A4A]" />
                )}
              </Button>

              {/* Add to My Subs Button */}
              {user && !isOwnService && (
                <Button
                  onClick={handleAddToMySubs}
                  disabled={addingToSubs}
                  className={`absolute top-4 right-16 shadow-md ${
                    isInMySubs 
                      ? "bg-[#1A4D2E] text-white hover:bg-[#143D24]" 
                      : "bg-[#E07A5F] text-white hover:bg-[#D06A4F]"
                  }`}
                  data-testid="add-to-subs-btn"
                >
                  {isInMySubs ? (
                    <>
                      <UserCheck className="w-4 h-4 mr-2" />
                      In My Subs
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add to My Subs
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Service Info */}
            <div className="p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="flex-1">
                  <h1 className="text-2xl lg:text-3xl font-bold text-[#1A1A1A] mb-3">
                    {service.name}
                  </h1>
                  <RatingDisplay
                    rating={service.average_rating || 0}
                    reviewCount={service.review_count || 0}
                    size="lg"
                  />
                  <p className="text-[#4A4A4A] mt-4 text-lg">
                    {service.description}
                  </p>
                </div>

                {/* Contact Card */}
                <div className="lg:w-80 bg-stone-50 rounded-xl p-6">
                  <h3 className="font-semibold text-[#1A1A1A] mb-4">Contact Info</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-[#1A4D2E] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[#1A1A1A]">{service.address}</p>
                        <p className="text-[#71717A]">{service.city}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-[#1A4D2E]" />
                      <a href={`tel:${service.phone}`} className="text-[#1A1A1A] hover:text-[#1A4D2E]">
                        {service.phone}
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-[#1A4D2E]" />
                      <a href={`mailto:${service.email}`} className="text-[#1A1A1A] hover:text-[#1A4D2E]">
                        {service.email}
                      </a>
                    </div>
                    {service.website && (
                      <div className="flex items-center gap-3">
                        <Globe className="w-5 h-5 text-[#1A4D2E]" />
                        <a
                          href={service.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#1A1A1A] hover:text-[#1A4D2E]"
                        >
                          Visit Website
                        </a>
                      </div>
                    )}
                    {service.hours && (
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-[#1A4D2E] flex-shrink-0 mt-0.5" />
                        <p className="text-[#1A1A1A]">{service.hours}</p>
                      </div>
                    )}
                  </div>

                  <Button
                    className="w-full mt-6 bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-full btn-hover-lift"
                    data-testid="contact-btn"
                    asChild
                  >
                    <a href={`tel:${service.phone}`}>Call Now</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Personal Notes Section (for logged-in users) */}
          {user && (
            <div className="bg-amber-50 rounded-2xl shadow-sm border border-amber-200 p-6 lg:p-8 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <StickyNote className="w-5 h-5 text-amber-600" />
                  <h2 className="text-lg font-bold text-[#1A1A1A]">My Notes</h2>
                  <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Private</span>
                </div>
                {!editingNote && existingNote && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingNote(true)}
                    className="text-amber-700 hover:text-amber-800"
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                )}
              </div>
              
              {editingNote || !existingNote ? (
                <div className="space-y-3">
                  <Textarea
                    value={personalNote}
                    onChange={(e) => setPersonalNote(e.target.value)}
                    placeholder="Add private notes about this contractor... (e.g., pricing, availability, quality of work, contact person)"
                    className="min-h-[100px] bg-white border-amber-300 focus:border-amber-500"
                    data-testid="personal-note-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveNote}
                      disabled={savingNote}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="save-note-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {savingNote ? "Saving..." : "Save Note"}
                    </Button>
                    {existingNote && (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setPersonalNote(existingNote.note);
                          setEditingNote(false);
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg p-4 border border-amber-200">
                  <p className="text-[#4A4A4A] whitespace-pre-wrap">{existingNote.note}</p>
                  <p className="text-xs text-[#71717A] mt-3">
                    Last updated: {new Date(existingNote.updated_at).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Reviews Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#1A1A1A]">
                Reviews ({reviews.length})
              </h2>
              {!user ? (
                <Button
                  onClick={() => {
                    const redirectUrl = window.location.href;
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  variant="outline"
                  className="border-[#1A4D2E] text-[#1A4D2E] rounded-full"
                  data-testid="sign-in-to-review-btn"
                >
                  Sign In to Review
                </Button>
              ) : isOwnService ? (
                <span className="text-sm text-[#71717A]">This is your listing</span>
              ) : hasUserReviewed ? (
                <span className="text-sm text-[#71717A]">You've already reviewed</span>
              ) : (
                <Button
                  onClick={() => setShowReviewForm(!showReviewForm)}
                  className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full"
                  data-testid="write-review-btn"
                >
                  Write a Review
                </Button>
              )}
            </div>

            {/* Review Form */}
            {showReviewForm && (
              <form onSubmit={handleSubmitReview} className="mb-8 p-6 bg-stone-50 rounded-xl">
                <h3 className="font-semibold text-[#1A1A1A] mb-4">Write Your Review</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#4A4A4A] mb-2">
                    Your Rating
                  </label>
                  <StarRating
                    rating={reviewRating}
                    size="lg"
                    interactive
                    onRate={setReviewRating}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#4A4A4A] mb-2">
                    Your Review
                  </label>
                  <Textarea
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="Share your experience..."
                    className="min-h-[120px]"
                    required
                    data-testid="review-comment-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={submittingReview}
                    className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full"
                    data-testid="submit-review-btn"
                  >
                    {submittingReview ? "Submitting..." : "Submit Review"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowReviewForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* Reviews List */}
            {reviews.length === 0 ? (
              <div className="text-center py-12">
                <Star className="w-12 h-12 text-stone-200 mx-auto mb-4" />
                <p className="text-[#71717A]">No reviews yet. Be the first to review!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((review) => (
                  <div
                    key={review.review_id}
                    className="border-b border-stone-100 pb-6 last:border-0"
                    data-testid={`review-${review.review_id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-medium text-[#1A1A1A]">{review.user_name}</p>
                        <StarRating rating={review.rating} size="sm" />
                      </div>
                      <span className="text-sm text-[#71717A]">
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-[#4A4A4A]">{review.comment}</p>
                    
                    {review.provider_response && (
                      <div className="mt-4 ml-4 pl-4 border-l-2 border-[#1A4D2E]/20">
                        <p className="text-sm font-medium text-[#1A4D2E] mb-1">
                          Provider Response:
                        </p>
                        <p className="text-[#4A4A4A] text-sm">{review.provider_response}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ServiceProfile;
